import { Role, User } from '../types'
import { uid } from '../utils/format'
import { supabase } from './supabaseClient'
import { ensureDefaultContent } from './repair'
import { hydrate, resetStore } from './store'

export interface Session {
  user: User
  organizationId: string
  /** Nombre de la empresa, usado al completar el contenido inicial. */
  organizationName?: string
  role: Role
}

export interface RegisterPayload {
  firstName: string
  lastName: string
  email: string
  password: string
  companyName: string
  sector: string
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
}

/** Ya no existen credenciales de demostración: la autenticación es real. */
export const DEMO_CREDENTIALS = { email: '', password: '' }

/**
 * Datos del registro que hacen falta para crear la organización más tarde.
 * Se guardan en los metadatos del usuario (y como copia local) porque el
 * formulario de registro no vuelve a pedirlos.
 */
export interface PendingSetup {
  companyName: string
  sector: string
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
}

const PENDING_KEY = 'ava:pending-setup'

function readPendingSetup(metadata?: Record<string, unknown>): PendingSetup | null {
  const fromMetadata =
    metadata && typeof metadata.company_name === 'string'
      ? {
          companyName: metadata.company_name as string,
          sector: (metadata.sector as string) ?? '',
          primaryColor: metadata.primary_color as string | undefined,
          secondaryColor: metadata.secondary_color as string | undefined,
        }
      : null

  let fromStorage: PendingSetup | null = null
  try {
    const raw = window.localStorage.getItem(PENDING_KEY)
    if (raw) fromStorage = JSON.parse(raw) as PendingSetup
  } catch {
    fromStorage = null
  }

  if (!fromMetadata && !fromStorage) return null
  return {
    companyName: fromMetadata?.companyName || fromStorage?.companyName || 'Mi empresa',
    sector: fromMetadata?.sector || fromStorage?.sector || '',
    primaryColor: fromMetadata?.primaryColor ?? fromStorage?.primaryColor,
    secondaryColor: fromMetadata?.secondaryColor ?? fromStorage?.secondaryColor,
    // El logotipo solo viaja en la copia local (los metadatos no admiten
    // archivos grandes).
    logoUrl: fromStorage?.logoUrl,
  }
}

function clearPendingSetup(): void {
  try {
    window.localStorage.removeItem(PENDING_KEY)
  } catch {
    /* almacenamiento no disponible */
  }
}

/**
 * Crea la organización si el usuario todavía no pertenece a ninguna.
 *
 * `create_organization_for_user` depende de `auth.uid()`, así que solo puede
 * ejecutarse con una sesión activa. Cuando el proyecto exige confirmar el
 * email, esa sesión no existe al registrarse: por eso la creación se hace
 * aquí, en el primer acceso autenticado (tras confirmar el correo o en el
 * primer inicio de sesión), usando los datos guardados en el registro.
 */
async function ensureOrganization(userId: string): Promise<boolean> {
  const { data: auth } = await supabase.auth.getUser()
  if (!auth.user) return false

  const pending = readPendingSetup(
    auth.user.user_metadata as Record<string, unknown>,
  )
  if (!pending) return false

  const { data: organizationId, error } = await supabase.rpc(
    'create_organization_for_user',
    { p_name: pending.companyName, p_sector: pending.sector },
  )
  if (error) throw new Error(error.message)

  const id = organizationId as string
  await seedDefaultContent(id, pending.companyName)

  if (pending.primaryColor || pending.logoUrl) {
    await supabase
      .from('organizations')
      .update({
        logo_url: pending.logoUrl ?? null,
        primary_color: pending.primaryColor ?? null,
        secondary_color: pending.secondaryColor ?? null,
      })
      .eq('id', id)
  }

  clearPendingSetup()
  return true
}

async function findMembership(userId: string) {
  const { data, error } = await supabase
    .from('organization_members')
    .select('organization_id, role, status')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('created_at')
    .limit(1)
    .maybeSingle()
  if (error) throw new Error(error.message)
  return data
}

async function buildSession(userId: string): Promise<Session> {
  // El perfil puede no existir todavía si la cuenta se confirmó por email.
  const { data: auth } = await supabase.auth.getUser()
  const metadata = (auth.user?.user_metadata ?? {}) as Record<string, string>
  await supabase.from('profiles').upsert({
    id: userId,
    first_name: metadata.first_name ?? '',
    last_name: metadata.last_name ?? '',
    email: auth.user?.email ?? '',
  })

  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()

  let membership = await findMembership(userId)

  // Primer acceso tras confirmar el email: aún no hay organización.
  if (!membership && (await ensureOrganization(userId))) {
    membership = await findMembership(userId)
  }

  if (!membership) {
    throw new Error(
      'Tu cuenta todavía no pertenece a ninguna empresa. Vuelve a registrarte o pide una invitación a tu equipo.',
    )
  }

  const user: User = {
    id: userId,
    firstName: profile?.first_name ?? '',
    lastName: profile?.last_name ?? '',
    email: profile?.email ?? '',
    avatarUrl: profile?.avatar_url ?? undefined,
    createdAt: profile?.created_at ?? new Date().toISOString(),
  }

  await hydrate(membership.organization_id)

  const { data: organization } = await supabase
    .from('organizations')
    .select('name')
    .eq('id', membership.organization_id)
    .maybeSingle()

  return {
    user,
    organizationId: membership.organization_id,
    organizationName: organization?.name ?? 'Mi empresa',
    role: membership.role as Role,
  }
}

export async function signIn(email: string, password: string): Promise<Session> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  })
  if (error) throw new Error('Email o contraseña incorrectos.')
  return buildSession(data.user.id)
}

/**
 * Registro real.
 *
 * Guarda el nombre de la empresa, el sector y la identidad visual en los
 * metadatos del usuario y en una copia local, porque la organización solo
 * puede crearse con una sesión autenticada. Si el proyecto exige confirmar
 * el email, Supabase no devuelve sesión aquí: en ese caso se devuelve
 * `null` y la organización se crea en el primer acceso con sesión activa.
 */
export async function signUp(payload: RegisterPayload): Promise<Session | null> {
  const pending: PendingSetup = {
    companyName: payload.companyName,
    sector: payload.sector,
    primaryColor: payload.primaryColor,
    secondaryColor: payload.secondaryColor,
    logoUrl: payload.logoUrl,
  }
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    /* almacenamiento no disponible: bastan los metadatos */
  }

  const { data, error } = await supabase.auth.signUp({
    email: payload.email.trim(),
    password: payload.password,
    options: {
      emailRedirectTo: `${window.location.origin}/login`,
      data: {
        first_name: payload.firstName,
        last_name: payload.lastName,
        company_name: payload.companyName,
        sector: payload.sector,
        primary_color: payload.primaryColor,
        secondary_color: payload.secondaryColor,
      },
    },
  })
  if (error) throw new Error(error.message)

  const userId = data.user?.id
  // Sin sesión: la cuenta necesita confirmarse por email.
  if (!userId || !data.session) return null

  return buildSession(userId)
}

/**
 * Crea el formulario predeterminado («Solicitud de estrategia») y la landing
 * inicial de una organización nueva, y los asocia entre sí.
 *
 * Es idempotente: si la organización ya tiene formularios, no crea nada, de
 * modo que puede ejecutarse más de una vez sin duplicar contenido.
 */
async function seedDefaultContent(
  organizationId: string,
  companyName: string,
): Promise<void> {
  // Una única implementación, compartida con la reparación de organizaciones
  // ya existentes, para que nuevos y antiguos usuarios reciban lo mismo.
  await ensureDefaultContent(organizationId, companyName)
}

/** Restaura la sesión guardada por Supabase al recargar la página. */
export async function restoreSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession()
  if (!data.session?.user) return null
  try {
    return await buildSession(data.session.user.id)
  } catch {
    return null
  }
}

/** Acepta una invitación de equipo y devuelve la organización. */
export async function acceptInvitation(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_invitation', {
    p_token: token,
  })
  if (error) throw new Error(error.message)
  return data as string
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (!email.includes('@')) throw new Error('Introduce un email válido.')
  // El enlace del correo debe abrir la pantalla donde se crea la contraseña
  // nueva, no el login: esta URL tiene que estar autorizada en
  // Supabase → Authentication → URL Configuration → Redirect URLs.
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/restablecer-contrasena`,
  })
  if (error) {
    const status = (error as { status?: number }).status
    if (status === 429) {
      throw new Error('Demasiados intentos. Espera unos minutos e inténtalo de nuevo.')
    }
    throw new Error(`No hemos podido enviar el correo: ${error.message}`)
  }
}

/**
 * Guarda la contraseña nueva. Solo funciona con la sesión temporal de
 * recuperación que crea Supabase al abrir el enlace del correo.
 */
export async function updatePassword(password: string): Promise<void> {
  if (password.length < 8) {
    throw new Error('La contraseña debe tener al menos 8 caracteres.')
  }
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw new Error(error.message)
}

/** Actualiza el nombre del usuario que ha iniciado sesión. */
export async function updateProfile(
  userId: string,
  firstName: string,
  lastName: string,
): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({
      full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
    })
    .eq('id', userId)
  if (error) throw new Error(error.message)
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut()
  resetStore()
}

/** Evita un import sin uso cuando el navegador no expone crypto.randomUUID. */
export function newId(prefix = 'id'): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : uid(prefix)
}
